import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { IconDownload } from 'assets/icons';
import { generalApi, GeneralApiTagTypes } from 'core/api';
import { useLazyGetAttachmentFileQuery } from 'core/attachments/api';
import { useAppDispatch } from 'hooks';
import { Button, ButtonProps } from 'modules/ui';
import { performFileDownload } from 'utils/performFileDownload';

export interface IDownloadAttachmentFileButtonProps extends ButtonProps {
    attachmentId: number;
    hideText?: boolean;
}

export const DownloadAttachmentFileButton: React.FC<IDownloadAttachmentFileButtonProps> = ({
    attachmentId,
    hideText,
    disabled,
    ...props
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [getAttachmentFile, { isFetching }] = useLazyGetAttachmentFileQuery();

    const handleDownload = async () => {
        try {
            const { data, headers } = await getAttachmentFile({ attachmentId, download: true }, false).unwrap();
            dispatch(
                generalApi.util.invalidateTags([
                    { type: GeneralApiTagTypes.attachment.item },
                    { type: GeneralApiTagTypes.attachment.list },
                    { type: GeneralApiTagTypes.invoice.list },
                    { type: GeneralApiTagTypes.commission.list },
                ]),
            );
            const fileName = headers.get('Content-Disposition')?.split('filename=')[1] || '';
            performFileDownload(data, decodeURIComponent(fileName));
        } catch (error: any) {
            if (error?.data?.message) {
                return toast.error(error.data.message);
            }
            toast.error(t('attachments.download.error'));
        }
    };
    return (
        <>
            <Button
                Icon={hideText ? <IconDownload /> : undefined}
                type="button"
                variant="secondary"
                onClick={handleDownload}
                disabled={isFetching || disabled}
                isLoading={isFetching}
                {...props}
            >
                {!hideText && <IconDownload />}
                {!hideText && t('common.subheader.download')}
            </Button>
        </>
    );
};
